import { Box, Grid, TextField, MenuItem } from "@mui/material";
import React from "react";
import ALTTextField from "../../../common/input-fields/ALTTextField";
import { useState, useEffect, useMemo, useCallback } from "react";
import { FieldVariants } from "../../../common/input-fields/utils/appInputenum";
import ActionButton from "../../../common/input-fields/ActionButton";
import SelectField from "../../../common/input-fields/SelectField";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DDQCard from "../components/DDQQuestions";
import DDQQuestions from "../components/DDQQuestions";
import answeredIcon from "../../../../assets/due-deligenece/answeredIcon.svg";
import flaggedIcon from "../../../../assets/due-deligenece/flaggedIcon.svg";
import pendingIcon from "../../../../assets/due-deligenece/pendingIcon.svg";
import SearchIcon from "@mui/icons-material/Search";
import PageHeader from "../../../common/PageHeader";
import { navigationOptions } from "../../../common/navigation-bars/navigationOptions";
import secureLocalStorage from "react-secure-storage";
import { getDDQuestionerDataByCompanyId, getDDQSummaryAnalytics } from "../services/apiClient";

const DDQDashboard = () => {
  
  //#region variables
  const companyId = secureLocalStorage.getItem("selectedCompanyId");
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    answerStatus: "",
  });
  const [ddqData, setDdqData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [analyticsData, setAnalyticsData] = useState({
    pendingQuestions: 0,
    aiAnsweredQuestions: 0,
    manuallyAnsweredQuestions: 0,
    reviewedQuestions: 0,
    flaggedQuestions: 0
  });

  //#region debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        search: searchTerm
      }));
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  //#region change events
  const handleChange = (name, value) => {
    if (name === "search") {
      setSearchTerm(value);
    } else {
      setFilters({
        ...filters,
        [name]: value,
      });
    }
  };

  //#region API calls
  const fetchDDQData = async () => {
    try {
      setLoading(true);
      const response = await getDDQuestionerDataByCompanyId(companyId);
      if (response.status === 200 && response.data?.success) {
        // Set finalAnswer to aiAnswer as requested
        const processedData = response.data.data.map(item => ({
          ...item,
          finalAnswer: item.aiAnswer
        }));
        setDdqData(processedData);
      } else {
        setDdqData([]);
      }
    } catch (error) {
      console.error("Error fetching DDQ data:", error);
      setDdqData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      const response = await getDDQSummaryAnalytics(companyId);
      if (response.status === 200 && response.data?.success) {
        setAnalyticsData(response.data.data);
      } else {
        setAnalyticsData({
          pendingQuestions: 0,
          aiAnsweredQuestions: 0,
          manuallyAnsweredQuestions: 0,
          reviewedQuestions: 0,
          flaggedQuestions: 0
        });
      }
    } catch (error) {
      console.error("Error fetching analytics data:", error);
      setAnalyticsData({
        pendingQuestions: 0,
        aiAnsweredQuestions: 0,
        manuallyAnsweredQuestions: 0,
        reviewedQuestions: 0,
        flaggedQuestions: 0
      });
    }
  };

  //#region useEffect
  useEffect(() => {
    if (companyId) {
      fetchDDQData();
      fetchAnalyticsData();
    }
  }, [companyId]);

  //#region status
  const summaryItems = useMemo(() => {
    const answered = analyticsData.aiAnsweredQuestions + 
                    analyticsData.manuallyAnsweredQuestions + 
                    analyticsData.reviewedQuestions;
    const flagged = analyticsData.flaggedQuestions;
    const pending = analyticsData.pendingQuestions;
    
    return [
      { icon: answeredIcon, label: "Answered", value: answered },
      { icon: flaggedIcon, label: "Flagged", value: flagged },
      { icon: pendingIcon, label: "Pending", value: pending },
    ];
  }, [analyticsData]);

  //#region get unique categories
  const uniqueCategories = useMemo(() => {
    const categories = new Set();
    ddqData.forEach(item => {
      if (item.pillar) categories.add(item.pillar);
    });
    return Array.from(categories).sort();
  }, [ddqData]);

  //#region return
  return (
    <Box p={2}>
      <PageHeader
        title="Due Diligence Questionnaire"
        description="AI-generated questions with automated answers"
        selectOptions={navigationOptions}
      />
      <Grid container spacing={2}>
        <Grid item size={{ xs: 12 }}>
          <div className="white-card">
            <div size={{ xs: 12 }} xs={12} sx={{ pl: 0, pr: 0 }}>
              <b>Summary</b>
            </div>
            <Grid container spacing={2} pl={8} mt={2} pr={8}>
              {summaryItems.map((item, index) => (
                <Grid
                  size={{ xs: 12, md: 4 }}
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems:
                      index === 0
                        ? "flex-start"
                        : index === 1
                        ? "center"
                        : "flex-end",
                  }}
                >
                  <Box className="child-row-margin-10" sx={{display: "flex", alignItems: "center"}}>
                    <img src={item?.icon} width="40" height="40" />
                    <div className="child-margin-5">
                      <Box
                        sx={{
                          fontWeight: 700,
                          fontSize: 28,
                          color: "#111827",
                          mb: 0.5,
                        }}
                      >
                        {item.value}
                      </Box>
                      <Box
                        sx={{ fontWeight: 500, color: "#111827", fontSize: 16 }}
                      >
                        {item.label}
                      </Box>
                    </div>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </div>
        </Grid>
        <Grid item size={{ xs: 12 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "end",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 3, alignItems: "end" }}>
              <Box>
                <Box sx={{ fontWeight: 500, mb: 1 }}>Filters</Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box>
                    <TextField
                      variant="outlined"
                      name="search"
                      placeholder="Search questions"
                      value={searchTerm}
                      onChange={(e) => handleChange("search", e.target.value)}
                      size="small"
                      InputProps={{ startAdornment: <SearchIcon /> }}
                    />
                  </Box>
                  <Box>
                    <TextField
                      select
                      variant="outlined"
                      name="category"
                      label="Category"
                      value={filters.category}
                      onChange={(e) => handleChange("category", e.target.value)}
                      size="small"
                      sx={{ minWidth: 150 }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {uniqueCategories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box>
                    <TextField
                      select
                      variant="outlined"
                      name="answerStatus"
                      label="Answer Status"
                      value={filters.answerStatus}
                      onChange={(e) =>
                        handleChange("answerStatus", e.target.value)
                      }
                      size="small"
                      sx={{ minWidth: 150 }}
                    >
                      <MenuItem value="">All Questions</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="answered">Answered</MenuItem>
                      <MenuItem value="ai_answered">AI Answered</MenuItem>
                      <MenuItem value="manually_answered">Manually Answered</MenuItem>
                      <MenuItem value="reviewed">Reviewed</MenuItem>
                      <MenuItem value="flagged">Flagged</MenuItem>
                    </TextField>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <ActionButton
                variant="contained"
                label="Download"
                icon={<ArrowDownwardIcon />}
                onClick={() => {}}
              />
              <ActionButton
                variant="contained"
                label="Add Question"
                icon={<AddIcon />}
                onClick={() => {}}
              />
            </Box>
          </Box>
        </Grid>
        <Grid item size={{ xs: 12 }}>
          <DDQQuestions companyId={companyId} filters={filters} ddqData={ddqData} loading={loading} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default DDQDashboard;
